import React, { useCallback } from "react";
import PropTypes from "prop-types";
import Tooltip from "~/assets/js/components/Tooltip";
import {
    WidgetContainer,
    TopImageContainer,
    Content,
    Name,
    Data,
    Symbols,
    Footer,
    StyledIconButton,
    TopImage,
    SoldOut,
    WidthEnsurer,
    SoldOutBackdrop,
    SoldOutTopImage,
} from "./style";
import GlutenFree from "~/static/images/icons/gluten-free.svg";
import LowCarb from "~/static/images/icons/low-carb.svg";
import SugarFree from "~/static/images/icons/sugar-free.svg";
import Vegan from "~/static/images/icons/vegan.svg";
import Vegetarian from "~/static/images/icons/vegetarian.svg";
import AddButton from "~/assets/js/components/Button/AddButton";
import { formatFloat, formatPrice } from "~/assets/js/Helpers/util";
import { getTotalEnergy, calculateFranchisePrice } from "../../Helpers/util";
import IconButton from "../Button/IconButton";
import Plus from "~/static/images/icons/plus2.svg";
import Minus from "~/static/images/icons/minus2.svg";

import LazyLoad from "react-lazyload";

const Widget = (props) => {
    const onClick = useCallback(() => {
        if (props.amount === 0) props.onAdd && props.onAdd();
        else if (props.amount === 1) {
            if (props.onDelete) props.onDelete();
            else if (props.onReset) props.onReset();
        }
    }, [props.amount]);

    if (!props.ingredient) return null;

    const price = props.storeId
        ? calculateFranchisePrice(props.storeId, props.ingredient)
        : props.ingredient.price;

    let details = [];
    if (props.ingredient.details.energy)
        details.push(`${Math.round(getTotalEnergy(props.ingredient))} kcal`);
    if (props.ingredient.details.co2)
        details.push(
            <>
                {formatFloat(
                    Math.round(props.ingredient.details.co2 / 10) / 100
                )}{" "}
                kg CO<sub>2</sub>e
            </>
        );
    if (props.ingredient.details.water)
        details.push(
            `${formatFloat(
                Math.round(props.ingredient.details.water / 10) / 100
            )}l`
        );
    let finalDetails = [];
    if (details.length > 0) finalDetails.push(details[0]);
    for (let i = 1; i < details.length; i++) {
        finalDetails.push(<> &#8226; </>);
        finalDetails.push(details[i]);
    }

    return (
        <WidgetContainer
            onClick={onClick}
            amount={props.amount || 0}
            soldOut={props.soldOut}
            widgetState={props.amount === 0 ? "add" : "remove"}
        >
            <LazyLoad height={100}>
                <TopImage
                    soldOut={props.soldOut}
                    transparentImage={props.transparentImage}
                    style={{
                        backgroundImage: `url(${props.ingredient.images.normal})`,
                    }}
                />
            </LazyLoad>
            {props.soldOut ? (
                <TopImageContainer
                    soldOut={props.soldOut}
                    transparentImage={props.transparentImage}
                    style={{
                        backgroundColor: "transparent",
                        alignItems: "flex-start",
                    }}
                >
                    {props.transparentImage ? null : (
                        <SoldOutTopImage
                            transparentImage={props.transparentImage}
                        />
                    )}
                </TopImageContainer>
            ) : null}
            <TopImageContainer
                soldOut={props.soldOut}
                transparentImage={props.transparentImage}
                className={props.amount === 0 ? "" : "visible"}
            >
                {!props.binary && (
                    <>
                        <StyledIconButton
                            className={props.amount === 0 ? "" : "visible"}
                            style={{ marginLeft: -14 }}
                        >
                            <IconButton
                                rounded
                                icon={Minus}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (props.amount === 0) onClick();
                                    else props.onRemove && props.onRemove();
                                }}
                            />
                        </StyledIconButton>
                        {props.amount === 0 ? null : props.amount}
                        <StyledIconButton
                            className={props.amount === 0 ? "" : "visible"}
                            style={{ marginRight: -14 }}
                        >
                            <IconButton
                                rounded
                                icon={Plus}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (props.amount === 0) onClick();
                                    else props.onAdd && props.onAdd();
                                }}
                            />
                        </StyledIconButton>
                    </>
                )}
            </TopImageContainer>
            <Symbols>
                {(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "vegan"
                ) ? (
                    <Tooltip text="Vegan">
                        <img src={Vegan} />
                    </Tooltip>
                ) : null}
                {(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "vegetarisch"
                ) &&
                !(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "vegan"
                ) ? (
                    <Tooltip text="Vegetarisch">
                        <img src={Vegetarian} />
                    </Tooltip>
                ) : null}
                {(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "zuckerfrei"
                ) ? (
                    <Tooltip text="Zuckerfrei">
                        <img src={SugarFree} />
                    </Tooltip>
                ) : null}
                {(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "low-carb"
                ) ? (
                    <Tooltip text="Low-Carb">
                        <img src={LowCarb} />
                    </Tooltip>
                ) : null}
                {(props.ingredient.attributes || props.ingredients.tags).find(
                    (x) => x === "glutenfrei"
                ) ? (
                    <Tooltip text="Glutenfrei">
                        <img src={GlutenFree} />
                    </Tooltip>
                ) : null}
            </Symbols>
            <Content>
                <Name>{props.ingredient.name}</Name>
                <Data>{finalDetails}</Data>
            </Content>
            <Footer>
                {!props.binary && formatPrice(price)}
                <div style={{ flex: 1, height: 30 }} />
                {!props.soldOut && (
                    <AddButton
                        onClick={(e) => {
                            e.stopPropagation();
                            if (props.amount === 0) {
                                onClick();
                            } else {
                                props.onReset && props.onReset();
                            }
                        }}
                        buttonState={props.amount === 0 ? "add" : "remove"}
                    />
                )}
            </Footer>
            {props.soldOut ? (
                <>
                    <SoldOutBackdrop />
                    <SoldOut>bald wieder verfügbar</SoldOut>
                </>
            ) : null}
            <WidthEnsurer />
        </WidgetContainer>
    );
};

Widget.defaultProps = {
    amount: 0,
    soldOut: false,
};

Widget.propTypes = {
    ingredient: PropTypes.any,
    amount: PropTypes.number,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
    onReset: PropTypes.func,
    onDelete: PropTypes.func,
    binary: PropTypes.bool,
    soldOut: PropTypes.bool,
    transparentImage: PropTypes.bool,
    ingredients: PropTypes.object,
};

export default Widget;
